import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Store } from 'lucide-react';
import { 
  getRestaurantById, 
  updateRestaurant, 
  getAllDeliverySlots,
  getRestaurantDeliverySlots 
} from '../../service/restaurant';
import { getRestaurantId } from '../../utils/JwtUtil';
import SettingsHeader from './SettingsHeader';
import SettingsTabs from './SettingsTabs';
import SettingsContent from './SettingsContent';
import FloatingSaveButton from './FloatingSaveButton';
import { useSettingsForm } from './hooks/useSettingsForm';

const DAYS_OF_WEEK = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];

export default function SettingsContainer() {
  const { t, i18n } = useTranslation();
  const [restaurant, setRestaurant] = useState(null);
  const [originalData, setOriginalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [allDeliverySlots, setAllDeliverySlots] = useState([]);
  const [selectedDeliverySlots, setSelectedDeliverySlots] = useState([]);
  const [activeTab, setActiveTab] = useState('basic');

  const {
    formData,
    setFormData,
    handleInputChange,
    handleWorkingDayToggle,
    handleTagAdd,
    handleTagRemove
  } = useSettingsForm();

  // Check if there are unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    if (!originalData) return false;
    return JSON.stringify(formData) !== JSON.stringify(originalData);
  }, [formData, originalData]);

  // Load restaurant data
  const loadRestaurantData = useCallback(async () => {
    try {
      setLoading(true);
      const restaurantId = getRestaurantId();
      if (!restaurantId) {
        console.error('Restaurant ID not found in JWT');
        return;
      }

      const data = await getRestaurantById(restaurantId, i18n.language);
      setRestaurant(data);
      
      // Map response to form data
      const mappedData = {
        nameEn: data.nameEn || '',
        nameAr: data.nameAr || '',
        descriptionEn: data.descriptionEn || '',
        descriptionAr: data.descriptionAr || '',
        address: data.address || '',
        phone: data.phone || '',
        latitude: data.latitude || '',
        longitude: data.longitude || '',
        imageUrl: data.imageUrl || '',
        backgroundImageUrl: data.backgroundImageUrl || '',
        rating: data.rating || 5.0,
        workingDays: data.workingDays || [],
        openingTime: data.openingTime || '',
        closingTime: data.closingTime || '',
        cliqAlias: data.cliqAlias || '',
        cliqAliasDisplayedName: data.cliqAliasDisplayedName || '',
        iban: data.iban || '',
        minimumDaysBeforeSubscriptionCreation: data.minimumDaysBeforeSubscriptionCreation || 2,
        mustSelectMealsBeforeDays: data.mustSelectMealsBeforeDays || 2,
        tags: data.tags || []
      };
      
      setFormData(mappedData);
      setOriginalData(mappedData);

      // Load restaurant delivery slots
      if (data.id) {
        const restaurantSlots = await getRestaurantDeliverySlots(data.id);
        setSelectedDeliverySlots(restaurantSlots.map(slot => slot.id));
      }
    } catch (error) {
      console.error('Error loading restaurant data:', error);
    } finally {
      setLoading(false);
    }
  }, [i18n.language, setFormData]);

  const loadDeliverySlots = useCallback(async () => {
    try {
      const slots = await getAllDeliverySlots();
      setAllDeliverySlots(slots);
    } catch (error) {
      console.error('Error loading delivery slots:', error);
    }
  }, []);

  useEffect(() => {
    loadRestaurantData();
    loadDeliverySlots();
  }, [loadRestaurantData, loadDeliverySlots]);

  const handleDeliverySlotToggle = (slotId) => {
    setSelectedDeliverySlots(prev =>
      prev.includes(slotId)
        ? prev.filter(id => id !== slotId)
        : [...prev, slotId]
    );
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      if (!restaurant?.id) {
        alert(t('settings.noRestaurantError'));
        return;
      }

      // Helper function to convert time string (HH:mm) to Time object format
      const formatTimeForBackend = (timeString) => {
        console.log('Formatting time:', timeString);
        if (!timeString || timeString.trim() === '') return null;
        // If it's already in HH:mm:ss format, return as is
        if (timeString.split(':').length === 3) return timeString;
        // Convert HH:mm to HH:mm:ss format
        if (timeString.includes(':') && timeString.split(':').length === 2) {
          const formatted = `${timeString}:00`;
          console.log('Formatted time result:', formatted);
          return formatted;
        }
        return null;
      };

      console.log('Raw form data times:', {
        openingTime: formData.openingTime,
        closingTime: formData.closingTime
      });

      // Prepare the payload according to RestaurantRequestDto
      const payload = {
        nameEn: formData.nameEn,
        nameAr: formData.nameAr,
        descriptionEn: formData.descriptionEn || '',
        descriptionAr: formData.descriptionAr || '',
        address: formData.address,
        phone: formData.phone,
        // Keep latitude and longitude as strings (as per DTO)
        latitude: formData.latitude?.toString() || null,
        longitude: formData.longitude?.toString() || null,
        imageUrl: formData.imageUrl || null,
        backgroundImageUrl: formData.backgroundImageUrl || null,
        ownerId: restaurant.ownerId, // Use the existing owner ID
        rating: parseFloat(formData.rating) || 5.0,
        tags: formData.tags || [],
        // Convert working days from strings to DayOfWeek enum format
        workingDays: formData.workingDays || [],
        // Format time fields properly for java.sql.Time
        openingTime: formatTimeForBackend(formData.openingTime),
        closingTime: formatTimeForBackend(formData.closingTime),
        // Payment information for restaurant transfers
        cliqAlias: formData.cliqAlias || null,
        cliqAliasDisplayedName: formData.cliqAliasDisplayedName || null,
        iban: formData.iban || null,
        // Subscription settings
        minimumDaysBeforeSubscriptionCreation: parseInt(formData.minimumDaysBeforeSubscriptionCreation) || 2,
        mustSelectMealsBeforeDays: parseInt(formData.mustSelectMealsBeforeDays) || 2,
        // Delivery slots - send only the IDs of selected slots
        deliverySlotIds: selectedDeliverySlots || []
      };

      console.log('Sending payload:', payload); // Debug log

      // Update restaurant data
      const updatedRestaurant = await updateRestaurant(restaurant.id, payload, i18n.language);
      
      // Update local state with response
      setRestaurant(updatedRestaurant);
      
      // Show success message
      alert(t('settings.saveSuccess'));
      
      // Optionally reload the page data to ensure consistency
      await loadRestaurantData();
      
    } catch (error) {
      console.error('Error saving restaurant:', error);
      alert(t('settings.saveError') + ': ' + (error.response?.data?.message || error.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center">
        <div className="text-center bg-white/80 backdrop-blur-sm p-8 rounded-2xl shadow-xl">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent mx-auto"></div>
          <p className="mt-6 text-lg text-gray-600">{t('settings.loading')}</p>
        </div>
      </div>
    );
  }

  if (!restaurant) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center">
        <div className="text-center bg-white/80 backdrop-blur-sm p-8 rounded-2xl shadow-xl">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Store className="w-8 h-8 text-gray-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('settings.noRestaurant')}</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <SettingsHeader 
        onSave={handleSave}
        saving={saving}
        hasUnsavedChanges={hasUnsavedChanges}
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 overflow-hidden">
          <div className="p-8">
            <SettingsTabs 
              activeTab={activeTab}
              onTabChange={setActiveTab}
            />
            
            <div className="min-h-[500px]">
              <SettingsContent
                activeTab={activeTab}
                formData={formData}
                restaurant={restaurant}
                allDeliverySlots={allDeliverySlots}
                selectedDeliverySlots={selectedDeliverySlots}
                onInputChange={handleInputChange}
                onWorkingDayToggle={handleWorkingDayToggle}
                onDeliverySlotToggle={handleDeliverySlotToggle}
                onTagAdd={handleTagAdd}
                onTagRemove={handleTagRemove}
                constants={{
                  DAYS_OF_WEEK
                }}
              />
            </div>
          </div>
        </div>
      </div>
      
      <FloatingSaveButton
        onSave={handleSave}
        saving={saving}
        hasUnsavedChanges={hasUnsavedChanges}
        show={true}
      />
    </div>
  );
}
